/**
 * What a store event says as a toast (`ToastSpec`), or null — only what the player must act on or
 * cares about: WEAPONS RELEASED and CAPITALS EXPOSED, eliminations (any nation), a betrayal of us
 * (and our own break's cost), a ceasefire broken on us, our capital recovered, gifts to us, a mark
 * from our ally, the answer to our own proposals (accepted or declined), the outcome of our strikes
 * (one card a salvo; warheads that landed after the target was out are told as wasted).
 * Our losses and our air fights come as one card a wave (`Waves`: `DAMAGE · RADAR, SAM ×2 LOST`).
 * Not news: our own orders (confirmed by the pointer), other nations' diplomacy and betrayals
 * (the results chronicle has them), fighter scrambles, the leader beacon, FINAL 3:00.
 * The card's header is one of a fixed set — OFFER, NEWS, DAMAGE, DEFENCE, STRIKE, BETRAYAL, CHAT —
 * the nation it is from / about only as its swatch (and named in the line); the card's colour is
 * what the news means (`tone`: green good, red loss / danger, cyan ally / offer, amber warning, dim info).
 * Pure (tested in test/toasts.test.ts, test/mechanics.test.ts).
 */
import { BALANCE, TICK_HZ, type PlayerId } from '@sa/sim';
import { clock, strikeText } from './alerts';
import type { GameEvent, GameStore } from './store';
import type { ToastSpec } from './toasts';
import { fmtInt } from '../ui/dom';

/** announcements of the match's phases: on screen a little longer, never shown stale */
const PHASE = { title: 'NEWS', ms: 7_000, maxWaitMs: 30_000, priority: 5 } as const;

/** a DAMAGE card always gets a slot: it outranks other news (a moment still comes first) */
const DAMAGE_PRIORITY = 8;

const LOST_NAME: Partial<Record<string, string>> = { sub: 'SUBMARINE' };

/** The betrayed side's moment (one text, so a new alliance can drop it unseen). */
export const betrayalText = (name: string) => `${name} BETRAYED YOU`;

/** how long the breaker of an alliance / a ceasefire may not strike its ex-partner (§2) */
const LOCK = () => clock(BALANCE.alliance.breakNoTargetTicks / TICK_HZ);
const CF_LOCK = () => clock(BALANCE.diplomacy.ceasefireBreakNoTargetTicks / TICK_HZ);

/** Our (human) ally fell, online: the ALLY chat line stays between us. */
export const fallenAllyCard = (ally: PlayerId, name: string): ToastSpec => ({ title: 'NEWS', text: `${name} IS OUT · STILL ON YOUR ALLY LINE`, tone: 'ally', from: ally, key: `fell:${ally}` });

export function newsFor(e: GameEvent, store: Pick<GameStore, 'view' | 'me'>, nameOf: (p: PlayerId) => string): ToastSpec | null {
  const v = store.view;
  const me = store.me;
  const from = (p: PlayerId, text: string, tone: ToastSpec['tone'], extra: Partial<ToastSpec> = {}): ToastSpec => ({ title: 'NEWS', text, tone, from: p, ...extra });
  switch (e.type) {
    case 'phase':
      if (e.kind === 'release') return { ...PHASE, text: 'WEAPONS RELEASED', tone: 'warn', sub: 'SILOS, SUBS AND BOMBERS MAY STRIKE' };
      if (e.kind === 'capitalsExposed') return { ...PHASE, text: v.eliminated ? 'CAPITALS EXPOSED' : 'CAPITALS EXPOSED · DEFEND THE CAPITAL', tone: 'warn' };
      // (FINAL 3:00 and the leader beacon: not news; the final minute is the clock's)
      return null;
    case 'eliminated': {
      // ours has the full-screen moment
      if (e.player === me) return null;
      const by = e.by && e.by !== e.player ? ` · BY ${e.by === me ? 'YOU' : nameOf(e.by)}` : '';
      return { title: 'NEWS', text: `${nameOf(e.player)} ${e.decapitated ? 'DECAPITATED' : 'ELIMINATED'}${by}`, tone: e.by === me ? 'ok' : 'info', from: e.player, ms: 7_000, priority: 4 };
    }
    case 'ally': {
      const who = nameOf(e.with);
      if (e.on) return from(e.with, `ALLIED WITH ${who} · SHARED VISION`, 'ally', { sub: 'NO STRIKES BETWEEN YOU' });
      // the betrayed side: the big moment — never dropped unseen, and what it means now
      if (e.theyBroke)
        return {
          title: 'BETRAYAL',
          text: betrayalText(who),
          sub: `THEY CAN'T STRIKE YOU FOR ${LOCK()} · YOU CAN STRIKE THEM NOW · NO SHARED VISION`,
          tone: 'danger',
          from: e.with,
          kind: 'moment',
          ms: 10_000,
        };
      // ours: clear, not dramatic — what it costs us
      return from(e.with, `ALLIANCE BROKEN · ${who} · NO NEW ALLY FOR ${clock(BALANCE.alliance.breakNoAllyTicks / TICK_HZ)} · CAN'T STRIKE THEM FOR ${LOCK()}`, 'warn', { ms: 8_000 });
    }
    case 'ceasefire': {
      const who = nameOf(e.with);
      if (e.on) return from(e.with, `CEASEFIRE · ${who} · ${clock(BALANCE.diplomacy.ceasefireTicks / TICK_HZ)}`, 'ok');
      // (running out is no news: the nation menu shows it)
      if (e.ended) return null;
      if (e.by === me) return from(e.with, `CEASEFIRE BROKEN · ${who} · NO NEW CEASEFIRE FOR ${clock(BALANCE.diplomacy.ceasefireBreakCooldownTicks / TICK_HZ)}`, 'info', { sub: `CAN'T STRIKE THEM FOR ${CF_LOCK()}` });
      // broken on us: a smaller betrayal — a danger card, never dropped unseen
      return from(e.with, `${who} BROKE THE CEASEFIRE`, 'danger', { title: 'BETRAYAL', priority: 6, maxWaitMs: Infinity, ms: 8_000, sub: `THEY CAN'T STRIKE YOU FOR ${CF_LOCK()} · YOU CAN STRIKE THEM NOW` });
    }
    // the answer to something we asked
    case 'allyDeclined':
      return from(e.by, `${nameOf(e.by)} DECLINED THE ALLIANCE`, 'info');
    case 'ceasefireDeclined':
      return from(e.by, `${nameOf(e.by)} DECLINED THE CEASEFIRE`, 'info');
    case 'gift':
      return from(e.from, `${nameOf(e.from)} SENT YOU ${fmtInt(e.amount)}`, 'ok');
    case 'mark':
      // ours is confirmed by the pointer; our ally's is an ask to us
      return e.from === me ? null : from(e.from, `${nameOf(e.from)} ASKS YOU TO HIT ${nameOf(e.target)}`, 'ally');
    case 'critical':
      // ours critical is the alert line's (loud, persistent); a rival's critical is offered there (Q STRIKE)
      return e.player === me && !e.on ? { ...PHASE, text: 'CAPITAL RECOVERED', tone: 'ok', ms: 4_000 } : null;
    case 'strike': {
      // whose land it hit: named first, their swatch on the card
      const at = e.owner > 0 && e.owner !== me ? e.owner : 0;
      const wasted = e.wasted ?? 0;
      const text = strikeText(at ? `${nameOf(at)} · ${e.label}` : e.label, e.n, e.through, e.seen, wasted);
      return { title: 'STRIKE', text, tone: e.through > wasted ? 'ok' : 'info', ...(at ? { from: at } : {}) };
    }
    default:
      return null;
  }
}

/** a loss or air fight this soon after the last one (ms) joins its wave's card */
export const WAVE_MS = 8_000;

interface WaveEntry {
  /** the entry's words for its count (`SAM ×2`, `PARIS −2 LEVELS`) */
  say: (n: number) => string;
  n: number;
  /** our loss (vs. our kill / a city that held) */
  bad: boolean;
  /** a structure lost (listed together: `RADAR, SAM ×2 LOST`) */
  lost?: boolean;
}

/** `SAM` / `SAM ×2`; `FIGHTER ×2` + tail */
const counted = (head: string, tail = '') => (n: number) => `${head}${n > 1 ? ` ×${n}` : ''}${tail}`;

class Wave {
  readonly entries = new Map<string, WaveEntry>();
  /** the nations in it (one only: the card takes its colour) */
  readonly from = new Set<PlayerId>();
  constructor(
    readonly key: string,
    public lastMs: number,
  ) {}

  add(id: string, entry: Omit<WaveEntry, 'n'>, n = 1): void {
    const x = this.entries.get(id);
    if (x) x.n += n;
    else this.entries.set(id, { ...entry, n });
  }

  get bad(): boolean {
    return [...this.entries.values()].some((x) => x.bad);
  }
}

/**
 * Our losses and air fights as one card a wave instead of a card each: structures and city hits in
 * a DAMAGE card (`RADAR, SAM ×2 LOST · PARIS −1 LEVEL`), aircraft down on either side in an AIR
 * DEFENCE card (`FIGHTER ×2 DOWN · SAM · KESTREL`, under it `KESTREL BOMBER DOWN · OUR SAM`). Each
 * event returns the wave's card so far (the same `key`: `ToastQueue.upsert` rewrites the card in
 * place) or null when the event is not one of these. `alive(key)`: the wave's card is still queued
 * or up (else the next event starts a new wave).
 */
export class Waves {
  private damage: Wave | null = null;
  private air: Wave | null = null;
  private seq = 0;

  constructor(private nameOf: (p: PlayerId) => string) {}

  add(e: GameEvent, nowMs: number, alive: (key: string) => boolean): ToastSpec | null {
    switch (e.type) {
      case 'lost': {
        if (e.kind === 'fighter' || e.kind === 'bomber') {
          // an aircraft of ours shot down: what did it, and whose when we saw it
          const whose = e.by && e.by.owner > 0 ? ` · ${this.nameOf(e.by.owner)}` : '';
          const tail = ` DOWN${e.by ? ` · ${e.by.what}${whose}` : ''}`;
          const w = (this.air = this.wave(this.air, 'air', nowMs, alive));
          w.add(`l:${e.kind}${tail}`, { say: counted(e.kind.toUpperCase(), tail), bad: true });
          if (e.by && e.by.owner > 0) w.from.add(e.by.owner);
          return this.airCard(w);
        }
        const w = (this.damage = this.wave(this.damage, 'damage', nowMs, alive));
        if (e.kind === 'city') w.add(`c:${e.name}`, { say: () => `${e.name ?? 'CITY'} DESTROYED`, bad: true });
        else w.add(`s:${e.kind}`, { say: counted(e.name ?? LOST_NAME[e.kind] ?? e.kind.toUpperCase()), bad: true, lost: true });
        return this.damageCard(w);
      }
      case 'cityHit': {
        // levels add up over the wave: PARIS −2 LEVELS
        const w = (this.damage = this.wave(this.damage, 'damage', nowMs, alive));
        w.add(`h:${e.name}`, { say: (n) => `${e.name ?? 'CITY'} −${n} LEVEL${n > 1 ? 'S' : ''}`, bad: true }, e.levels);
        return this.damageCard(w);
      }
      case 'held': {
        const w = (this.damage = this.wave(this.damage, 'damage', nowMs, alive));
        w.add(`b:${e.name}`, { say: () => `${e.name ?? 'CITY'} HELD · BRACED`, bad: false });
        return this.damageCard(w);
      }
      case 'airKill': {
        const w = (this.air = this.wave(this.air, 'air', nowMs, alive));
        w.add(`k:${e.owner}:${e.kind}:${e.by}`, { say: counted(`${this.nameOf(e.owner)} ${e.kind.toUpperCase()}`, ` DOWN · OUR ${e.by}`), bad: false });
        w.from.add(e.owner);
        return this.airCard(w);
      }
      default:
        return null;
    }
  }

  private wave(w: Wave | null, kind: string, nowMs: number, alive: (key: string) => boolean): Wave {
    if (w && nowMs - w.lastMs <= WAVE_MS && alive(w.key)) {
      w.lastMs = nowMs;
      return w;
    }
    return new Wave(`${kind}:${++this.seq}`, nowMs);
  }

  private damageCard(w: Wave): ToastSpec {
    const xs = [...w.entries.values()];
    const lost = xs.filter((x) => x.lost).map((x) => x.say(x.n));
    const rest = xs.filter((x) => !x.lost).map((x) => x.say(x.n));
    const text = [...(lost.length ? [`${lost.join(', ')} LOST`] : []), ...rest].join(' · ');
    return { title: 'DAMAGE', text, tone: w.bad ? 'danger' : 'ok', key: w.key, priority: DAMAGE_PRIORITY };
  }

  private airCard(w: Wave): ToastSpec {
    const xs = [...w.entries.values()];
    const ours = xs.filter((x) => x.bad).map((x) => x.say(x.n));
    const kills = xs.filter((x) => !x.bad).map((x) => x.say(x.n));
    // our losses are the line (our kills under them) — or our kills when we lost nothing
    const text = (ours.length ? ours : kills).join(', ');
    const sub = ours.length && kills.length ? kills.join(', ') : undefined;
    const from = w.from.size === 1 ? [...w.from][0] : undefined;
    return { title: 'DEFENCE', text, tone: ours.length ? 'danger' : 'ok', key: w.key, ...(sub ? { sub } : {}), ...(from !== undefined ? { from } : {}) };
  }
}
